import React from 'react';
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArrowCounterClockwise } from 'phosphor-react';
import { toast } from 'sonner';
import { IEventoContrato, useEventos } from '../../../hooks/useEventos';
import { formatCurrency } from '../../../utils/FormatFunctions';

const STATUS_CONTRATO: Record<string, { color: string; label: string }> = {
  pago: { color: 'green', label: 'Pago' },
  nf_emitida: { color: 'purple', label: 'NF Emitida' },
  aguardando_nf: { color: 'purple', label: 'Aguardando NF' },
  assinado: { color: 'cyan', label: 'Assinado' },
  enviado_assinatura: { color: 'orange', label: 'Enviado p/ Ass.' },
  contrato: { color: 'blue', label: 'Contrato' },
  proposta: { color: 'yellow', label: 'Proposta' },
  cancelado: { color: 'gray', label: 'Cancelado' },
};

interface EventoArquivadosProps {
  eventoId: number;
}

/**
 * Chip "Arquivados (N)" do quadro do evento + modal com os contratos arquivados.
 *
 * O "Arquivar card" do quadro tira o card e marca o contrato como arquivado, por isso a
 * lista vem da API de contratos do evento (a de cards arquivados só enxerga o
 * arquivamento da legalização). Restaurar devolve o card na coluna em que estava.
 */
export const EventoArquivados: React.FC<EventoArquivadosProps> = ({ eventoId }) => {
  const queryClient = useQueryClient();
  const { getContratosArquivados, desarquivarContrato } = useEventos();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data, isLoading } = useQuery({
    queryKey: ['evento-contratos-arquivados', eventoId],
    queryFn: () => getContratosArquivados(eventoId),
  });
  // O filtro `arquivados=true` é novo no backend; o antigo ignora o parâmetro e devolve os ATIVOS. Sem este
  // filtro aqui, o admin no ar antes do backend mostraria contrato do quadro como arquivado (review de 17/09).
  const contratos: IEventoContrato[] = (data?.data ?? []).filter(c => c.arquivado);
  const total = contratos.length;

  const restaurarMutation = useMutation({
    mutationFn: (contratoId: number) => desarquivarContrato({ eventoId, contratoId }),
    onSuccess: () => {
      toast.success('Contrato restaurado para o quadro');
      if (contratos.length <= 1) onClose(); // era o último: o modal não fica aberto e vazio
      queryClient.invalidateQueries({ queryKey: ['evento-contratos-arquivados', eventoId] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
    },
    onError: () => toast.error('Não foi possível restaurar o contrato.'),
  });

  return (
    <>
      <Tooltip
        label={
          total === 0
            ? 'Nenhum contrato arquivado neste evento'
            : 'Ver e restaurar contratos arquivados'
        }
        openDelay={400}
        shouldWrapChildren // botão desabilitado não dispara o tooltip sozinho
      >
        <Button
          size="sm"
          fontSize="13px"
          variant="ghost"
          onClick={onOpen}
          isDisabled={total === 0}
          leftIcon={<Archive size={14} weight="duotone" />}
          borderRadius="full"
          color="gray.500"
          _hover={{ bg: 'blackAlpha.50', color: 'gray.700' }}
          fontWeight="500"
          flexShrink={0}
        >
          Arquivados ({isLoading ? '…' : total})
        </Button>
      </Tooltip>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader display="flex" alignItems="center" gap={2} fontSize="18px" fontWeight="700">
            <Archive size={18} weight="duotone" />
            Contratos arquivados
            <Tag size="sm" fontSize="10px" colorScheme="purple" borderRadius="full" ml={1}>
              {total}
            </Tag>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={5}>
            <Text fontSize="11px" color="gray.500" mb={4}>
              Saíram do quadro mas continuam preservados. Restaurar devolve o card à mesma coluna em
              que estava.
            </Text>

            {isLoading && (
              <Flex justify="center" py={10}>
                <Spinner size="lg" color="purple.500" thickness="3px" />
              </Flex>
            )}

            <Flex direction="column" gap={2}>
              {contratos.map(contrato => {
                const status = STATUS_CONTRATO[contrato.status];
                return (
                  <Box
                    key={contrato.id}
                    bg="white"
                    borderWidth="1px"
                    borderColor="gray.100"
                    borderRadius="lg"
                    px={4}
                    py={3}
                  >
                    <Flex align="flex-start" justify="space-between" gap={3}>
                      <Box minW={0} flex={1}>
                        <Text fontSize="13px" fontWeight="700" color="gray.800" noOfLines={1}>
                          {contrato.fornecedor_nome}
                        </Text>
                        <Flex align="center" gap={2} mt={1} flexWrap="wrap">
                          {contrato.kanban_coluna && (
                            <Tooltip label="Coluna em que estava ao ser arquivado" openDelay={400}>
                              <Tag
                                size="sm"
                                colorScheme="purple"
                                borderRadius="full"
                                fontSize="10px"
                              >
                                {contrato.kanban_coluna}
                              </Tag>
                            </Tooltip>
                          )}
                          <Tag
                            size="sm"
                            colorScheme={status?.color ?? 'gray'}
                            variant="subtle"
                            borderRadius="full"
                            fontSize="10px"
                          >
                            {status?.label ?? contrato.status}
                          </Tag>
                          {contrato.categoria_nome && (
                            <Text fontSize="11px" color="gray.400">
                              {contrato.categoria_nome}
                            </Text>
                          )}
                          {Number(contrato.valor) > 0 && (
                            <Text fontSize="11px" color="gray.500" fontWeight="500">
                              {formatCurrency(contrato.valor)}
                            </Text>
                          )}
                        </Flex>
                      </Box>

                      <Button
                        size="sm"
                        fontSize="13px"
                        variant="outline"
                        colorScheme="purple"
                        flexShrink={0}
                        leftIcon={<ArrowCounterClockwise size={14} weight="bold" />}
                        onClick={() => restaurarMutation.mutate(contrato.id)}
                        isLoading={
                          restaurarMutation.isPending && restaurarMutation.variables === contrato.id
                        }
                      >
                        Restaurar
                      </Button>
                    </Flex>
                  </Box>
                );
              })}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
